/**
 * CROSS-PAGE LINK AFFORDANCES — "should I even render this link?"
 *
 * THIS FILE NO LONGER CARRIES A POLICY TABLE. It used to export `ROUTE_ROLES`,
 * a static copy of the seeded role-name lists, and App.jsx gated every route on
 * it. That copy could not follow a runtime edit of the `role_permissions`
 * matrix, so after an administrator ticked or unticked a box the client would
 * offer a link the API refuses, or hide one the API would now allow.
 *
 * Since that table is gone:
 *   - route guarding is `<PermissionRoute page="…" />` (components/), which
 *     asks AuthContext what the SERVER says this session may open
 *     (GET /api/auth/me/permissions — the same `role_permissions` rows
 *     `requirePermission` enforces);
 *   - menu filtering is `filterMenuTreeByCapabilities` in menuData.js, off the
 *     same list;
 *   - and `canAccessRoute` below answers from that same list too.
 *
 * WHY THIS FUNCTION STILL EXISTS. Four built pages render cross-page links
 * (the navigation restored from the RawHTMLDesign headers, audit §3 X5) and
 * call `canAccessRoute(user, path)` to decide whether to render them at all.
 * Keeping the signature lets those pages keep working while their answer
 * silently becomes the live one. New code should prefer `const { can } =
 * useAuth()` and `can('some-page')`, which is the same answer without the
 * module-level register.
 *
 * `user` is now ignored — access is not a property of the role NAME any more.
 * It stays in the signature only so existing call sites need not change.
 *
 * IT IS STILL NOT THE GATE. Nothing here grants anything; the server checks
 * every request regardless. This only decides what to OFFER.
 */

/**
 * The capabilities AuthContext last read from the server, or null when they
 * are not (yet) known.
 *
 * A module-level register rather than a hook because the callers are plain
 * functions inside render bodies. AuthContext writes it inside its fetch,
 * BEFORE it sets the state that re-renders the tree, so any component that
 * renders as a result of capabilities arriving already sees them here — the
 * register can never lag the context it mirrors.
 */
let liveCapabilities = null;

/** Called only by AuthContext. `null` means "not known", never "holds nothing". */
export function setLiveCapabilities(capabilities) {
  liveCapabilities = capabilities || null;
}

/**
 * Paths that carry no PermissionRoute because the server does not gate them
 * either — the landing dashboard and the account-security page. Mirrors
 * ALWAYS_AVAILABLE_SLUGS in menuData.js and UNGATED_ROUTES in the backend's
 * permissionMap.js.
 */
const UNGATED_PATH_SLUGS = new Set(['change-password']);

/**
 * True when the signed-in session may open `path`.
 *
 * Unknown paths (anything that is not a page slug — `/`, `/change-password`,
 * status pages) are treated as open, matching the routing table: those are not
 * wrapped in a PermissionRoute and are genuinely ungated server-side too.
 *
 * While capabilities are unknown this returns FALSE for every real page: a
 * link that might not work is not offered, rather than guessed at. In practice
 * that window is not reachable — ProtectedRoute holds the authenticated subtree
 * on a spinner until the answer arrives — so it only bites when the capability
 * read genuinely failed, which is exactly when offering the link would be a lie.
 */
export function canAccessRoute(user, path) {
  const slug = typeof path === 'string' ? path.replace(/^\//, '') : '';
  if (!slug || slug.includes('/')) return true;
  if (UNGATED_PATH_SLUGS.has(slug)) return true;
  if (!liveCapabilities) return false;
  return liveCapabilities.has(`${slug}:view`);
}
